// Snapshot-export voor zowel 2D-kaart als 3D-scene (zie §7.4).
//
// Twee modi:
//  - Geen blok geselecteerd: alleen het map/3D-canvas via toDataURL.
//  - Wel een blok geselecteerd (en 2D-modus): de complete kaart-+-rechter-
//    paneel-area, geserialiseerd via html-to-image. Op die manier komt het
//    actieve eigenschappen-/resultatenpaneel mee in de PNG.
//
// In beide gevallen leggen we het watermerk uit §7.4 over de output.

import { toCanvas } from "html-to-image";
import { getMapInstance } from "../components/Map2D";
import { getView3DCanvas } from "../components/View3D";
import { useStore } from "../state/store";
import { dayOfYearToMonthDay } from "../shadow/sunPosition";
import { REFERENCE_YEAR } from "../state/store";

export type SnapshotMode = "2d" | "3d";

export async function downloadSnapshot(mode: SnapshotMode): Promise<void> {
  const out = await renderSnapshotCanvas(mode);
  const blob: Blob = await new Promise((resolve, reject) => {
    out.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("PNG-export mislukt."))),
      "image/png",
    );
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  const safeName =
    useStore.getState().scenario.name.replace(/[^a-z0-9-_]+/gi, "-").toLowerCase() ||
    "scenario";
  a.href = url;
  a.download = `schaduwtool-${safeName}-${stamp}.png`;
  a.click();
  URL.revokeObjectURL(url);
}

async function renderSnapshotCanvas(mode: SnapshotMode): Promise<HTMLCanvasElement> {
  const includePanel = useStore.getState().selectedBlockId !== null && mode === "2d";
  const out = document.createElement("canvas");

  if (includePanel) {
    // De wrapper-div in App.tsx markeren we met data-snapshot-area; die
    // bevat zowel de kaart als het rechterpaneel.
    const area = document.querySelector<HTMLElement>("[data-snapshot-area]");
    if (!area) throw new Error("Snapshot-gebied niet gevonden in DOM.");
    // html-to-image renderen met een vaste pixelRatio voor scherpe output
    // op high-DPI schermen, en een witte achtergrond voor het watermerk-
    // contrast.
    const captured = await toCanvas(area, {
      pixelRatio: window.devicePixelRatio > 1 ? 2 : 1.5,
      backgroundColor: "#ffffff",
      cacheBust: true,
      // Opzettelijk geen filter: heatmap-legenda en progress-bar mogen mee.
    });
    out.width = captured.width;
    out.height = captured.height;
    const ctx = out.getContext("2d");
    if (!ctx) throw new Error("Canvas-2D-context niet beschikbaar.");
    ctx.drawImage(captured, 0, 0);
  } else {
    const source = getSourceCanvas(mode);
    if (!source) {
      throw new Error(
        mode === "2d"
          ? "De kaart is nog niet beschikbaar."
          : "De 3D-weergave is nog niet geïnitialiseerd. Open eerst de 3D-modus.",
      );
    }
    out.width = source.width;
    out.height = source.height;
    const ctx = out.getContext("2d");
    if (!ctx) throw new Error("Canvas-2D-context niet beschikbaar.");
    ctx.drawImage(source, 0, 0);
  }

  drawWatermark(out);
  return out;
}

function getSourceCanvas(mode: SnapshotMode): HTMLCanvasElement | null {
  if (mode === "2d") {
    const map = getMapInstance();
    if (!map) return null;
    map.triggerRepaint();
    return map.getCanvas();
  }
  return getView3DCanvas();
}

function drawWatermark(canvas: HTMLCanvasElement): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;
  const state = useStore.getState();
  const { month, day } = dayOfYearToMonthDay(REFERENCE_YEAR, state.currentDayOfYear);
  const hour = Math.floor(state.currentMinuteOfDay / 60);
  const min = state.currentMinuteOfDay % 60;
  const dateStr = `${pad(day)}-${pad(month + 1)} ${pad(hour)}:${pad(min)}`;

  const lines = [
    `Schaduwtool — ${state.scenario.name}`,
    `${dateStr} (NL-kloktijd, referentiejaar ${REFERENCE_YEAR})`,
    "Indicatieve weergave — geen juridische waarde",
    "Luchtfoto © Beeldmateriaal Nederland / PDOK",
  ];

  const padX = 12;
  const padY = 10;
  const lineH = 16;
  const fontSize = 12;
  ctx.save();
  ctx.font = `${fontSize}px ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
  ctx.textBaseline = "top";

  let maxLineW = 0;
  for (const l of lines) {
    const m = ctx.measureText(l).width;
    if (m > maxLineW) maxLineW = m;
  }
  const boxW = maxLineW + padX * 2;
  const boxH = lines.length * lineH + padY * 2;
  const boxX = w - boxW - 12;
  const boxY = h - boxH - 12;

  ctx.fillStyle = "rgba(15, 23, 42, 0.82)";
  ctx.fillRect(boxX, boxY, boxW, boxH);
  ctx.strokeStyle = "rgba(255,255,255,0.25)";
  ctx.lineWidth = 1;
  ctx.strokeRect(boxX + 0.5, boxY + 0.5, boxW - 1, boxH - 1);

  ctx.fillStyle = "#ffffff";
  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i], boxX + padX, boxY + padY + i * lineH);
  }
  ctx.restore();
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}
